import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import DataTable from "../shared/DataTable";
import { useLocation, Link, useNavigate } from "react-router-dom";
import GlobalLoader from "../shared/GlobalLoader";

const PsmType = () => {
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!loc.state) {
      navigate('/dashboard', { replace: true });
    }
  }, [loc.state, navigate]);

  const location = useLocation();
  const { type, data } = location.state || {};

  // Header mapping configuration
  const headerMappings = {
    Pipes: {
      'pipeName': 'ID',
      'pipeTypeType': 'Type',
      'pipeSize': 'Pipe Diameter',
      'pipeLocation': 'Location',
      'pipeSeverity': 'Severity',
      'pipeDescription': 'Description'
    },
    Equipment: {
      'EquipmentName': 'ID',
      'EquipmentType': 'Type',
      'location': 'Location',
      'OverlaySeverity': 'Severity',
      'Description': 'Description'
    }
  };

  const [tableData, setTableData] = useState([]);
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "Pipes");
  const [headerName, setHeaderName] = useState("");
  const [tableHeader, setTableHeader] = useState([]);
  const [transformedData, setTransformedData] = useState({
    Pipes: [],
    Equipment: []
  });
  const [chartState, setChartState] = useState({
    series: [],
    options: {}
  });

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = localStorage.getItem("theme") || "light";
      setTheme(newTheme);
    };

    window.addEventListener("themeChange", handleThemeChange);
    return () => window.removeEventListener("themeChange", handleThemeChange);
  }, []);

  const isLight = theme === "light";

  // Transform API data to match display requirements
  const transformDataForDisplay = (apiData, dataType) => {
    const mapping = headerMappings[dataType];
    return apiData.map((item, index) => {
      const transformedItem = {};
      for (const [apiKey, displayKey] of Object.entries(mapping)) {
        transformedItem[displayKey] = item[apiKey] || '';
      }

      transformedItem.imageURL = item.imageURL || '';
      transformedItem.originalItem = item;
      transformedItem.isMailSent = item.isMailSent || false;
      transformedItem.sentEmailData = item.sentEmailData || [];
      transformedItem.id = item.id || `${dataType.toLowerCase()}-${index}`;

      return transformedItem;
    });
  };

  // Group data by all six severity levels
  const groupBySeverity = (items, severityKey) => {
    const groups = {
      "Level 1": [],
      "Level 2": [],
      "Level 3": [],
      "Level 4": [],
      "Level 5": [],
      "Level 0": [],
      "Other": []
    };

    items.forEach(item => {
      const severity = item[severityKey];
      if (severity.includes("Level 0")) {
        groups["Level 0"].push(item);
      } else if (severity.includes("Level 1")) {
        groups["Level 1"].push(item);
      } else if (severity.includes("Level 2")) {
        groups["Level 2"].push(item);
      } else if (severity.includes("Level 3")) {
        groups["Level 3"].push(item);
      } else if (severity.includes("Level 4")) {
        groups["Level 4"].push(item);
      } else if (severity.includes("Level 5")) {
        groups["Level 5"].push(item);
      } else {
        groups["Other"].push(item);
      }
    });

    // Filter out empty groups and map to ApexCharts format
    return Object.entries(groups)
      .filter(([_, items]) => items.length > 0)
      .map(([severity, items]) => ({
        label: getLabelForSeverity(severity),
        originalLabel: severity,
        value: items.length,
        color: getColorForSeverity(severity),
        details: items
      }));
  };

  const getColorForSeverity = (severity) => {
    if (severity.includes("Level 0")) return "#a19f9f";
    if (severity.includes("Level 1")) return "#f73153";
    if (severity.includes("Level 2")) return "#f3bb15";
    if (severity.includes("Level 3")) return "#9178e5";
    if (severity.includes("Level 4")) return "#178df3";
    if (severity.includes("Level 5")) return "#29e1a1";
    return "#999999";
  };

  const getLabelForSeverity = (severity) => {
    if (severity.includes("Level 1")) return "Action Required";
    if (severity.includes("Level 2")) return "Attention Required";
    if (severity.includes("Level 3")) return "Monitoring Required";
    if (severity.includes("Level 4")) return "Regular Check Required";
    if (severity.includes("Level 5")) return "Satisfactory";
    if (severity.includes("Level 0")) return "Validation Required";
    return severity;
  };

  const transformApiData = () => {
    if (!data) return;

    if (type !== "MI") {
      const pipeData = data.pipeData?.data || [];
      const transformedPipeData = transformDataForDisplay(pipeData, 'Pipes');
      const pipeSeverityGroups = groupBySeverity(transformedPipeData, 'Severity');
      setTransformedData(prev => ({ ...prev, Pipes: pipeSeverityGroups }));
    }

    const equipmentData = data.EquipementData?.data || [];
    const transformedEquipmentData = transformDataForDisplay(equipmentData, 'Equipment');
    const equipmentSeverityGroups = groupBySeverity(transformedEquipmentData, 'Severity');
    setTransformedData(prev => ({ ...prev, Equipment: equipmentSeverityGroups }));
  };

  useEffect(() => {
    if (data) {
      transformApiData();
    }
  }, [data]);

  useEffect(() => {
    if (transformedData[activeTab]?.length > 0) {
      const chartData = getChartData(activeTab);
      const series = chartData.map(item => item.value);
      const options = getChartOptions(activeTab);

      setChartState({
        series,
        options
      });
    }
  }, [activeTab, transformedData, theme]);

  const getChartData = (currentTab) => {
    return currentTab === "Pipes" ? transformedData.Pipes : transformedData.Equipment;
  };

  const getChartOptions = (currentTab) => {
    const chartData = getChartData(currentTab);

    return {
      chart: {
        type: 'donut',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const clickedIndex = config.dataPointIndex;
            const clickedItem = chartData[clickedIndex];

            if (clickedItem && clickedItem.details) {
              setTableData(clickedItem.details);
              setHeaderName(clickedItem.label);
              const currentHeaderMappings = headerMappings[currentTab];
              const headers = Object.keys(currentHeaderMappings).map(key => currentHeaderMappings[key]);
              setTableHeader(headers);
            }
          }
        }
      },
      labels: chartData.map(item => item.label),
      colors: chartData.map(item => item.color),
      stroke: {
        show: true,
        width: 2,
        colors: isLight ? '#FFFFFF' : '#1e1e2d',
      },
      dataLabels: {
        enabled: true
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '20px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: isLight ? '#000' : '#fff',
                offsetY: -10
              },
              value: {
                show: true,
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: isLight ? '#000' : '#fff',
                offsetY: 0,
                formatter: function (val) {
                  return val;
                }
              },
              total: {
                show: true,
                showAlways: true,
                fontSize: '12px',
                label: `${type} Punch List`,
                color: isLight ? '#000' : '#fff',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                }
              }
            }
          }
        }
      },
      fill: {
        type: 'gradient',
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '11px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 400,
        labels: {
          colors: isLight ? '#000' : '#fff'
        },
        markers: {
          width: 12,
          height: 12,
          strokeWidth: 0,
          strokeColor: '#fff',
          radius: 12,
          offsetX: -5
        },
        itemMargin: {
          horizontal: 10,
          vertical: 20
        },
        offsetX: 10,
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: isLight ? 'light' : 'dark',
        y: {
          formatter: function (value) {
            return value;
          }
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    };
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setTableData([]);
    setTableHeader(Object.values(headerMappings[tab]));
  };

  const renderCustomCell = (column, row) => {
    if (column === "ID" && row.imageURL) {
      return (
        <Link
          to={{
            pathname: "/dashboard/drawing",
            state: { imageURL: row.imageURL },
          }}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          {row.ID}
        </Link>
      );
    }

    if (column === "Severity") {
      const severity = row[column];
      let color = '#000000';
      if (severity.includes("Level 0")) color = '#cccccc';
      else if (severity.includes("Level 1")) color = '#ff0000';
      else if (severity.includes("Level 2")) color = '#ff6600';
      else if (severity.includes("Level 3")) color = '#ffcc00';
      else if (severity.includes("Level 4")) color = '#cc99ff';
      else if (severity.includes("Level 5")) color = '#00cc00';

      return <span style={{ color, fontWeight: 'bold' }}>{severity}</span>;
    }

    return row[column];
  };

  if (!data) {
    return <GlobalLoader />;
  }

  const shouldShowPipesTab = type !== "MI";

  return (
    <div>
      <div className="container-fluid gx-3">
        <div className="row rounded theme-container mx-3">
          <div className="col-12 d-flex align-items-center justify-content-end">
            <div>
              <ul
                className="nav nav-pills nav-custom-pills w-100 justify-content-center"
                id="pills-tab"
                role="tablist"
              >
                {shouldShowPipesTab && (
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link fw-bold ${activeTab === "Pipes" ? "active" : ""}`}
                      id="system-analysis-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#system-analysis"
                      type="button"
                      role="tab"
                      aria-controls="system-analysis"
                      aria-selected={activeTab === "Pipes" ? "true" : "false"}
                      onClick={() => handleTabClick("Pipes")}
                    >
                      Pipes
                    </button>
                  </li>
                )}
                <li className="nav-item ms-2" role="presentation">
                  <button
                    className={`nav-link fw-bold ${activeTab === "Equipment" ? "active" : ""}`}
                    id="pipe-analysis-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pipe-analysis"
                    type="button"
                    role="tab"
                    aria-controls="pipe-analysis"
                    aria-selected={activeTab === "Equipment" ? "true" : "false"}
                    onClick={() => handleTabClick("Equipment")}
                  >
                    Equipment
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-12 mt-3 mb-4 p-3 rounded">
            <div className="col-12 p-0">
              {(type !== "MI" || activeTab === "Equipment") && (
                <Chart
                  options={chartState.options}
                  series={chartState.series}
                  type="donut"
                  width="100%"
                  height="300px"
                />
              )}
            </div>
          </div>

          {Array.isArray(tableData) && tableData.length > 0 && (
            <div className="table-responsive">
              <DataTable
                headingText={headerName}
                headers={tableHeader}
                data={tableData}
                renderCustomCell={renderCustomCell}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PsmType;